import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiUrl } from '../lib/api'

export type AdminRoleTier =
  | 'a1_platform_owner'
  | 'a2_internal_staff'
  | 'a2_ops'
  | 'a2_finance'
  | 'a2_readonly'
export type AdminMemberStatus = 'active' | 'suspended' | 'terminated'

export type AdminTeamMember = {
  membership_id: string
  user_id: string
  org_id: string
  display_name: string
  email: string
  persona_label: string
  tier: AdminRoleTier
  user_status: string
  membership_status: AdminMemberStatus
  accepted_at: string | null
  /** Acceptance time, or creation time for members added without accepting. */
  joined_at: string
  last_login_at: string | null
  updated_at: string
  base_capabilities: string[]
  revoked_capabilities: string[]
  effective_capabilities: string[]
  is_self: boolean
  can_edit_access: boolean
  can_suspend: boolean
  can_remove: boolean
  protection_reason: string | null
}

export type AdminInvitation = {
  invitation_id: string
  email: string
  tier: AdminRoleTier
  status: string
  created_at: string
  expires_at: string
  accepted_at: string | null
  revoked_at: string | null
}

export type PlatformDirectoryMember = {
  membership_id: string
  user_id: string
  user_label: string
  email: string | null
  user_status: string
  org_id: string
  org_name: string
  org_type: string
  org_role_label: string | null
  tier: string
  status: string
  effective_capabilities: string[]
  granted_at: string
  // Seller (BO2) member sees no money amounts.
  prices_hidden?: boolean
}

type Page<T> = { items: T[]; total: number; page: number; per_page: number }
type DirectoryResponse = { users: PlatformDirectoryMember[]; meta: { total: number } }


export type AdminTeamAuditEvent = {
  id: string
  event_type: string
  occurred_at: string
  actor_user_id: string | null
  actor_persona: string | null
  detail: {
    reason?: string
    revoked_session_count?: number
    before?: Record<string, unknown>
    after?: Record<string, unknown>
  }
}

export type AdminTeamAuditResponse = { events: AdminTeamAuditEvent[]; next_cursor: string | null }

export class AdminUsersApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
  }
}

async function request(path: string, init?: RequestInit): Promise<unknown> {
  const response = await fetch(apiUrl(path), {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  })
  if (!response.ok) {
    let message = `Request failed (${response.status})`
    try {
      const body = await response.json() as { detail?: string | { message?: string } }
      message = typeof body.detail === 'string' ? body.detail : body.detail?.message ?? message
    } catch { /* keep the stable fallback */ }
    throw new AdminUsersApiError(response.status, message)
  }
  if (response.status === 204) return null
  return response.json()
}

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function stringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string')
}

function assertMember(value: unknown): asserts value is AdminTeamMember {
  if (!record(value)) throw new Error('Admin team member contract drift')
  const strings = ['membership_id', 'user_id', 'org_id', 'display_name', 'email', 'persona_label', 'tier', 'user_status', 'membership_status', 'joined_at', 'updated_at']
  const booleans = ['is_self', 'can_edit_access', 'can_suspend', 'can_remove']
  if (strings.some((key) => typeof value[key] !== 'string') || booleans.some((key) => typeof value[key] !== 'boolean') || !stringArray(value.base_capabilities) || !stringArray(value.revoked_capabilities) || !stringArray(value.effective_capabilities)) {
    throw new Error('Admin team member contract drift')
  }
}

function page<T>(value: unknown, assertItem: (item: unknown) => asserts item is T): Page<T> {
  if (!record(value) || !Array.isArray(value.items) || ['total', 'page', 'per_page'].some((key) => typeof value[key] !== 'number')) throw new Error('Admin team list contract drift')
  value.items.forEach(assertItem)
  return value as Page<T>
}

function assertInvitation(value: unknown): asserts value is AdminInvitation {
  if (!record(value) || ['invitation_id', 'email', 'tier', 'status', 'created_at', 'expires_at'].some((key) => typeof value[key] !== 'string')) throw new Error('Admin invitation contract drift')
}

function assertDirectory(value: unknown): asserts value is DirectoryResponse {
  if (!record(value) || !Array.isArray(value.users) || !record(value.meta) || typeof value.meta.total !== 'number') throw new Error('Platform directory contract drift')
  for (const item of value.users) {
    if (!record(item) || ['membership_id', 'user_id', 'user_label', 'org_id', 'org_name', 'org_type', 'tier', 'status', 'granted_at'].some((key) => typeof item[key] !== 'string') || !stringArray(item.effective_capabilities)) throw new Error('Platform directory contract drift')
  }
}

export async function getAdminTeam(): Promise<Page<AdminTeamMember>> {
  return page(await request('/admin/users/team'), assertMember)
}

async function updateAdminTeamMember(membershipId: string, input: { tier?: AdminRoleTier; status?: 'active' | 'suspended'; expected_updated_at: string; reason: string }): Promise<AdminTeamMember> {
  const value = await request(`/admin/users/team/${encodeURIComponent(membershipId)}`, { method: 'PATCH', body: JSON.stringify(input) })
  assertMember(value)
  return value
}

export async function replaceAdminTeamAccess(membershipId: string, input: { revoked_capabilities: string[]; expected_updated_at: string; reason: string }): Promise<AdminTeamMember> {
  const value = await request(`/admin/users/team/${encodeURIComponent(membershipId)}/access`, { method: 'PUT', body: JSON.stringify(input) })
  assertMember(value)
  return value
}

export async function removeAdminTeamMember(membershipId: string, input: { expected_updated_at: string; reason: string }): Promise<void> {
  await request(`/admin/users/team/${encodeURIComponent(membershipId)}`, { method: 'DELETE', body: JSON.stringify(input) })
}

export async function getAdminTeamInvitations(): Promise<Page<AdminInvitation>> {
  // Only invitations still awaiting an answer; the server drops expired ones and
  // people who already joined the team without accepting.
  return page(await request('/admin/users/team/invitations?status=pending'), assertInvitation)
}

async function resendAdminInvitation(invitationId: string): Promise<void> {
  await request(`/admin/invitations/${encodeURIComponent(invitationId)}/resend`, { method: 'POST' })
}

async function revokeAdminInvitation(invitationId: string): Promise<void> {
  await request(`/admin/invitations/${encodeURIComponent(invitationId)}/revoke`, { method: 'POST' })
}

export async function getPlatformDirectory(): Promise<DirectoryResponse> {
  const value = await request('/admin/users/directory')
  assertDirectory(value)
  return value
}

async function setDirectoryMemberPricesHidden(membershipId: string, hidden: boolean): Promise<void> {
  await request(`/admin/users/directory/${membershipId}/prices`, { method: 'PUT', body: JSON.stringify({ hidden }) })
}

export const adminUsersKeys = {
  team: ['admin-users', 'team'] as const,
  invitations: ['admin-users', 'invitations'] as const,
  directory: ['admin-users', 'directory'] as const,
}

export function useAdminTeam() { return useQuery({ queryKey: adminUsersKeys.team, queryFn: getAdminTeam }) }
export function useAdminTeamInvitations() { return useQuery({ queryKey: adminUsersKeys.invitations, queryFn: getAdminTeamInvitations }) }
export function usePlatformDirectory() { return useQuery({ queryKey: adminUsersKeys.directory, queryFn: getPlatformDirectory }) }

export function useDirectoryPricesMutation() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ membershipId, hidden }: { membershipId: string; hidden: boolean }) => setDirectoryMemberPricesHidden(membershipId, hidden),
    onSuccess: () => client.invalidateQueries({ queryKey: adminUsersKeys.directory }),
  })
}

export function useAdminMemberAudit(membershipId: string | null) {
  return useQuery({
    queryKey: [...adminUsersKeys.team, 'audit', membershipId],
    queryFn: () => request(`/admin/users/team/${membershipId}/audit?limit=25`) as Promise<AdminTeamAuditResponse>,
    enabled: Boolean(membershipId),
  })
}

export function useAdminMemberMutation() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ membershipId, input }: { membershipId: string; input: Parameters<typeof updateAdminTeamMember>[1] }) => updateAdminTeamMember(membershipId, input),
    onSuccess: () => client.invalidateQueries({ queryKey: adminUsersKeys.team }),
  })
}

export function useAdminAccessMutation() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ membershipId, input }: { membershipId: string; input: Parameters<typeof replaceAdminTeamAccess>[1] }) => replaceAdminTeamAccess(membershipId, input),
    onSuccess: () => client.invalidateQueries({ queryKey: adminUsersKeys.team }),
  })
}

export function useAdminRemoveMutation() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ membershipId, input }: { membershipId: string; input: Parameters<typeof removeAdminTeamMember>[1] }) => removeAdminTeamMember(membershipId, input),
    onSuccess: () => client.invalidateQueries({ queryKey: adminUsersKeys.team }),
  })
}

export function useAdminInvitationLifecycle() {
  const client = useQueryClient()
  const invalidate = () => client.invalidateQueries({ queryKey: adminUsersKeys.invitations })
  const resend = useMutation({ mutationFn: resendAdminInvitation, onSuccess: invalidate })
  const revoke = useMutation({ mutationFn: revokeAdminInvitation, onSuccess: invalidate })
  return { resend, revoke }
}

// --- Role presets (Roles tab) --------------------------------------------

export type AdminTeamRole = {
  tier: string
  editable: boolean
  member_count: number
  capabilities: string[]
}

export type AdminTeamRoleList = {
  items: AdminTeamRole[]
  capability_universe: string[]
}

function assertRoleList(value: unknown): asserts value is AdminTeamRoleList {
  if (!record(value) || !Array.isArray(value.items) || !stringArray(value.capability_universe)) {
    throw new AdminUsersApiError(500, 'Unexpected role preset payload')
  }
}

async function getAdminTeamRoles(): Promise<AdminTeamRoleList> {
  const value = await request('/admin/users/team/roles')
  assertRoleList(value)
  return value
}

export function useAdminTeamRoles() {
  return useQuery({ queryKey: [...adminUsersKeys.team, 'roles'] as const, queryFn: getAdminTeamRoles })
}

export function useAdminRolePresetMutation() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ tier, input }: { tier: string; input: { capabilities: string[]; change_reason: string } }) =>
      request(`/admin/users/team/roles/${tier}`, { method: 'PUT', body: JSON.stringify(input) }) as Promise<AdminTeamRole>,
    // A preset edit changes every member's base set → refresh both the roles
    // view and the team/permissions views.
    onSuccess: () => client.invalidateQueries({ queryKey: adminUsersKeys.team }),
  })
}
